import React from 'react';
import { Link } from 'react-router-dom';
import { usePermissions } from '../contexts/PermissionsContext';
import { PERMISSIONS, PermissionKey } from '../types/permissions';
import MissionControlBackdrop from '../components/MissionControlBackdrop';
import {
  LayoutGrid, CalendarClock, ClipboardList, CalendarDays, FilePen, Users,
  BrainCircuit, MessageSquare, Sparkles, Upload, MapPin, Wrench, Mail,
  FileText, Shield, Activity, Lock, BookOpen, Send, Terminal,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Item {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  show: boolean;
}

interface Section {
  title: string;
  accent: string;
  blurb: string;
  items: Item[];
}

// ── Item card ─────────────────────────────────────────────────────────────────

function ItemCard({ item, accent }: { item: Item; accent: string }) {
  return (
    <Link
      to={item.to}
      className="card"
      style={{
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: `${accent}1a`,
          border: `1px solid ${accent}44`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: accent,
        }}
      >
        {item.icon}
      </div>
      <div style={{ fontWeight: 650, fontSize: 15 }}>{item.title}</div>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.6 }}>{item.description}</div>
    </Link>
  );
}

// ── Landing page ──────────────────────────────────────────────────────────────

export default function LandingPage() {
  const perms = usePermissions();
  const { hasPermission, isAdmin } = perms;
  const can = (p: PermissionKey) => hasPermission(p);

  const sections: Section[] = [
    {
      title: 'Admin HQ',
      accent: '#7aa2ff',
      blurb: 'The operational core — track every active return through its workflow.',
      items: [
        {
          to: '/portal/jobs', icon: <LayoutGrid size={19} />, title: 'Return Jobs', show: can(PERMISSIONS.NAV_PORTAL),
          description: 'Active returns grouped into urgency lanes by due date — Overdue, Due Today, Due This Week and Due in 2 Weeks — with status, VIP flags, missing MSEL/SOW warnings and a return summary viewer.',
        },
        {
          to: '/portal/extensions', icon: <CalendarClock size={19} />, title: 'Returns to Extend', show: can(PERMISSIONS.NAV_PORTAL),
          description: 'Returns that need an extension, organized by the same urgency lanes so nothing slips past its deadline.',
        },
        {
          to: '/portal/followup', icon: <ClipboardList size={19} />, title: 'Follow-Up Hub', show: can(PERMISSIONS.NAV_PORTAL) && can(PERMISSIONS.NAV_FOLLOWUP),
          description: 'Coordinate client follow-ups. A return is locked to one person while they work it, so two people never contact the same client at once.',
        },
        {
          to: '/pto', icon: <CalendarDays size={19} />, title: 'PTO Dashboard', show: can(PERMISSIONS.NAV_PORTAL) && can(PERMISSIONS.NAV_PTO),
          description: 'Time-off calendar and entries, plus phone, front desk and Admin Team coverage.',
        },
      ],
    },
    {
      title: 'Power of Attorney',
      accent: '#57d38c',
      blurb: 'The full IRS Form 2848 lifecycle, fully digital.',
      items: [
        {
          to: '/poa', icon: <ClipboardList size={19} />, title: 'All POAs', show: can(PERMISSIONS.NAV_POA),
          description: 'Track every Form 2848 from creation through signature, with automated notifications and reminders.',
        },
        {
          to: '/poa/new', icon: <FilePen size={19} />, title: 'New Form 2848', show: can(PERMISSIONS.NAV_POA),
          description: 'Create a new Form 2848, prefilled from firm systems, and send it out for signature.',
        },
        {
          to: '/poa/contractors', icon: <Users size={19} />, title: 'Contractors', show: can(PERMISSIONS.NAV_POA),
          description: 'A register of representatives who are not BMSS employees, so they can be picked on a 2848 like anyone else.',
        },
      ],
    },
    {
      title: 'AI Tools',
      accent: '#64d2ff',
      blurb: 'AI assistance for reviewing returns and answering tax questions.',
      items: [
        {
          to: '/ai-review', icon: <BrainCircuit size={19} />, title: 'Return Review', show: can(PERMISSIONS.NAV_AI_REVIEW),
          description: 'AI quality-control review of returns for completeness and issues before filing, delivered as a memo to the people you choose.',
        },
        {
          to: '/ai-review/chat', icon: <MessageSquare size={19} />, title: 'Return Chat', show: can(PERMISSIONS.NAV_AI_CHAT),
          description: 'Ask plain-English questions about a specific return and get answers from its actual data.',
        },
        {
          to: '/ai-review/askkeith', icon: <Sparkles size={19} />, title: 'AskKeith', show: can(PERMISSIONS.NAV_ASK_KEITH),
          description: 'Tax-knowledge chat backed by the firm’s tax research library.',
        },
      ],
    },
    {
      title: 'Data Tools',
      accent: '#ffd166',
      blurb: 'Bulk operations against Practice Engine and related utilities.',
      items: [
        {
          to: '/import', icon: <Upload size={19} />, title: 'Import Data', show: can(PERMISSIONS.NAV_IMPORT),
          description: 'Bulk updates to Practice Engine from CSV or SQL — client attributes, staff budgets, job creation and updates, and client records.',
        },
        {
          to: '/import/addresses', icon: <MapPin size={19} />, title: 'Address Validator', show: can(PERMISSIONS.NAV_IMPORT),
          description: 'Standardize client addresses, phones and emails to firm conventions, optionally verify against USPS, and push the fixes back to Practice Engine.',
        },
        {
          to: '/utility/ms990ext', icon: <Wrench size={19} />, title: 'MS 990 Batch Extension', show: can(PERMISSIONS.NAV_IMPORT),
          description: 'Run the one-off MS 990 batch extension through CCH.',
        },
        {
          to: '/utility/member-standards', icon: <Mail size={19} />, title: 'Member Standards Emails', show: can(PERMISSIONS.NAV_MEMBER_STANDARDS),
          description: 'The weekly Member Standards email rotation.',
        },
      ],
    },
    {
      title: 'Reports',
      accent: '#c084fc',
      blurb: 'Analytics and cross-system audits across CCH, M-Files, SafeSend and Practice Engine.',
      items: [
        {
          to: '/reports', icon: <FileText size={19} />, title: 'All Existing Reports', show: can(PERMISSIONS.NAV_REPORTS),
          description: 'Multi-state analytics, Logic App health, reconciliation audits and more. Each report is permissioned individually, so you will see the ones you have access to.',
        },
        {
          to: '/outsourced', icon: <FileText size={19} />, title: 'Returns Outsourced', show: can(PERMISSIONS.NAV_REPORTS) && can(PERMISSIONS.NAV_OUTSOURCED),
          description: 'Track returns prepared by third-party preparers.',
        },
      ],
    },
    {
      title: 'Administration',
      accent: '#f87171',
      blurb: 'Configuration, access control and system health.',
      items: [
        { to: '/admin/desks', icon: <LayoutGrid size={19} />, title: 'Desks & Assignments', show: perms.isAdminLeadership, description: 'Admin-team jobs and assignments.' },
        { to: '/admin/team', icon: <LayoutGrid size={19} />, title: 'Admin Team Dashboard', show: perms.isAdminLeadership || perms.isAdminTeam, description: 'The Admin Team inbox and its jobs.' },
        { to: '/admin/dev-team', icon: <Terminal size={19} />, title: 'Dev Team', show: isAdmin, description: 'Triage board for bug reports submitted from the 🐛 button on every page.' },
        { to: '/admin/token-management', icon: <Shield size={19} />, title: 'Token Management', show: isAdmin, description: 'CCH OAuth token status and manual refresh — the first place to look when return data will not load.' },
        { to: '/admin/data-integrity', icon: <Shield size={19} />, title: 'Data Integrity', show: isAdmin, description: 'Data-quality audit tools.' },
        { to: '/admin/health-check', icon: <Activity size={19} />, title: 'Automation Health', show: isAdmin, description: 'Health of the automations that run behind the scenes.' },
        { to: '/admin/security', icon: <Lock size={19} />, title: 'Security', show: isAdmin, description: 'Groups and the permission matrix that control who sees what.' },
        { to: '/admin/network-policies', icon: <Shield size={19} />, title: 'Network Policies', show: isAdmin, description: 'IP and network access rules.' },
        { to: '/admin/tax-research-rag', icon: <BookOpen size={19} />, title: 'Tax Research RAG', show: isAdmin, description: 'Manage the tax-knowledge library behind AskKeith.' },
        { to: '/admin/zoom-broadcast', icon: <Send size={19} />, title: 'Zoom Broadcast', show: isAdmin, description: 'Push messages to users.' },
      ],
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

      {/* ── Hero (mission-control backdrop runs 30s, then fades out) ──────── */}
      <div className="hero">
        <MissionControlBackdrop runForMs={30000} />

        <div className="hero-logo">
          <img
            src={`${process.env.PUBLIC_URL}/KeithBotfieldWebFavicon.svg`}
            alt="Admin HQ"
            style={{ width: 40, height: 40 }}
          />
        </div>

        <h1 className="hero-title">Admin HQ</h1>

        <p className="hero-tagline">
          BMSS’s tax-return operations dashboard. Track returns and extensions, manage Powers of Attorney,
          run AI reviews, and work with Practice Engine data — all in one place.
        </p>
      </div>

      {/* ── What's available ──────────────────────────────────────────────── */}
      {sections.map(section => {
        const items = section.items.filter(i => i.show);
        if (items.length === 0) return null;
        return (
          <section key={section.title} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: section.accent }}>{section.title}</h2>
              <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 2 }}>{section.blurb}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
              {items.map(item => <ItemCard key={item.to} item={item} accent={section.accent} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}
